import React, { Component } from 'react';
import gql from 'graphql-tag';
import { fromJS } from 'immutable';
import findIndex from 'lodash/findIndex';
import orderBy from 'lodash/orderBy';
import PropTypes from 'prop-types';
import { compose, graphql } from 'react-apollo';
import Tab from 'react-bootstrap/Tab';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import { SUBSCRIPTION_PREFERRED_LOCATION_CHANGED } from 'containers/DesktopNotificationTray';
import LoadingIndicator from 'components/LoadingIndicator';
import TabView from 'components/TabView';
import { LG_BREAKPOINT_AND_UP } from 'utils/constants';
import fragments from 'utils/graphql/fragments';
import { tournamentShape, tournamentsShape } from 'utils/shapes';
import {
  TOURNAMENT_EVENT_END,
  TOURNAMENT_EVENT_NOTIFY_START,
  TOURNAMENT_EVENT_START,
  TOURNAMENT_STATUS_COMPLETED,
  TOURNAMENT_STATUS_IN_PROGRESS,
  TOURNAMENT_STATUS_UPCOMING
} from 'utils/tournaments';
import { parseQuery } from 'utils/web';
import withMediaQuery from 'utils/withMediaQuery';
import TournamentListGroup from './TournamentListGroup';
import './TournamentList.css';

export const UPCOMING_TAB = 'upcoming';
export const IN_PROGRESS_TAB = 'inProgress';
export const COMPLETED_TAB = 'completed';
const TABS = [
  { key: UPCOMING_TAB, title: 'Upcoming' },
  { key: IN_PROGRESS_TAB, title: 'In Progress' },
  { key: COMPLETED_TAB, title: 'Past Tournaments' }
];

class TournamentList extends Component {
  constructor(props) {
    super(props);

    const params = parseQuery(this.props.location.search);
    const tabLocation = params.status;

    let defaultTab = UPCOMING_TAB;
    if (tabLocation === UPCOMING_TAB || tabLocation === COMPLETED_TAB) {
      defaultTab = tabLocation;
    }

    this.state = {
      defaultTab: defaultTab,
      latestNotifyStartId: undefined
    };

    this.unsubscribeFromNewData = () => {};
    this.handleNotifyStart = this.handleNotifyStart.bind(this);
    this.handleTabSelect = this.handleTabSelect.bind(this);
  }

  componentDidMount() {
    this.unsubscribeFromNewData = this.props.subscribeToTournamentEvents(
      this.handleNotifyStart
    );
    this.unsubscribeFromLocationData = this.props.subscribeToLocationData();
  }

  componentWillUnmount() {
    this.unsubscribeFromNewData();
    this.unsubscribeFromNewData = () => {};
    this.unsubscribeFromLocationData = () => {};
  }

  handleNotifyStart({ id, event }) {
    const data = this.props.data;
    const upcoming = data.upcoming && data.upcoming.tournaments;
    if (
      event === TOURNAMENT_EVENT_NOTIFY_START &&
      this.state.latestNotifyStartId !== id &&
      upcoming &&
      upcoming.find(t => t.id === id)
    ) {
      this.setState({ latestNotifyStartId: id });
    }
  }

  handleTabSelect(selectedTab) {
    trackUserEvent({
      activityType: SELECTION_EVENT,
      eventName: 'select-tournament-tab',
      eventValue: selectedTab,
      initiator: 'tournament-tabs'
    });
  }

  render() {
    const { data, tabViewProps, mobile, matchesBreakpoint } = this.props;
    const { defaultTab, latestNotifyStartId } = this.state;

    if (data.loading) {
      return mobile ? <LoadingIndicator expand={false} /> : null;
    }

    if (data.error) {
      return (
        <div className="tournament-list-error">
          <div className="alert alert-danger">
            There was a problem retrieving the list of tournaments. Please try
            again later.
          </div>
        </div>
      );
    }

    const inProgress =
      data.inProgress &&
      orderBy(data.inProgress.tournaments, ['startTime'], ['asc']).filter(
        t => t.game && t.game.enabled
      );
    const upcoming =
      data.upcoming &&
      orderBy(data.upcoming.tournaments, ['startTime'], ['asc']).filter(
        t => t.game && t.game.enabled
      );
    const completed =
      data.completed &&
      orderBy(data.completed.tournaments, ['endTime'], ['desc']);

    return (
      <TabView
        defaultTab={defaultTab}
        tabTitles={TABS}
        id="home-tournament"
        className="tournament-list-tab-view"
        navClassName="tournaments-navigation"
        dropdownClassName="tournament-list-menu"
        matchesBreakpoint={matchesBreakpoint}
        onSelectTab={this.handleTabSelect}
        {...tabViewProps}
      >
        <Tab.Content>
          <Tab.Pane eventKey={UPCOMING_TAB} mountOnEnter>
            <h3 className="sr-only">Upcoming</h3>
            <TournamentListGroup
              status={TOURNAMENT_STATUS_UPCOMING}
              tournaments={upcoming}
              latestNotifyStartId={latestNotifyStartId}
              hideButtons={!!mobile}
            />
          </Tab.Pane>
          <Tab.Pane eventKey={IN_PROGRESS_TAB} mountOnEnter>
            <h3 className="sr-only">In Progress</h3>
            <TournamentListGroup
              status={TOURNAMENT_STATUS_IN_PROGRESS}
              tournaments={inProgress}
              hideButtons={!!mobile}
            />
          </Tab.Pane>
          <Tab.Pane eventKey={COMPLETED_TAB} mountOnEnter>
            <h3 className="sr-only">Past Tournaments</h3>
            <TournamentListGroup
              status={TOURNAMENT_STATUS_COMPLETED}
              tournaments={completed}
            />
          </Tab.Pane>
        </Tab.Content>
      </TabView>
    );
  }
}

TournamentList.propTypes = {
  tabViewProps: PropTypes.object,
  mobile: PropTypes.bool,
  data: PropTypes.shape({
    loading: PropTypes.bool,
    upcoming: tournamentsShape,
    inProgress: tournamentsShape,
    completed: tournamentShape
  }),
  matchesBreakpoint: PropTypes.bool,
  subscribeToTournamentEvents: PropTypes.func.isRequired
};

const QUERY_GET_TOURNAMENTS = gql`
  query GetTournaments(
    $upcoming: GetTournaments!
    $inProgress: GetTournaments!
    $completed: GetTournaments!
  ) {
    upcoming: tournaments(tournaments: $upcoming) {
      tournaments {
        ...Tournament
      }
    }
    inProgress: tournaments(tournaments: $inProgress) {
      tournaments {
        ...Tournament
      }
    }
    completed: tournaments(tournaments: $completed) {
      tournaments {
        ...Tournament
        ...TournamentLeaderboard
      }
    }
  }
  ${fragments.tournament}
  ${fragments.tournamentLeaderboard}
`;

const SUBSCRIPTION_TOURNAMENT_EVENTS = gql`
  subscription TournamentEvents {
    tournamentEvents {
      id
      event
    }
  }
`;
export default compose(
  withMediaQuery(LG_BREAKPOINT_AND_UP),
  graphql(QUERY_GET_TOURNAMENTS, {
    options: {
      variables: {
        upcoming: { status: TOURNAMENT_STATUS_UPCOMING },
        inProgress: { status: TOURNAMENT_STATUS_IN_PROGRESS },
        completed: { status: TOURNAMENT_STATUS_COMPLETED }
      },
      fetchPolicy: 'cache-and-network'
    },
    props: ({ data }) => ({
      data,
      subscribeToTournamentEvents: onNotifyStart => {
        return data.subscribeToMore({
          document: SUBSCRIPTION_TOURNAMENT_EVENTS,
          updateQuery: (prev, { subscriptionData }) => {
            const { id, event } = subscriptionData.data;

            if (event === TOURNAMENT_EVENT_NOTIFY_START) {
              onNotifyStart && onNotifyStart(subscriptionData.data);
              return null;
            }

            const eventTransitions = {
              [TOURNAMENT_EVENT_START]: { from: 'upcoming', to: 'inProgress' },
              [TOURNAMENT_EVENT_END]: { from: 'inProgress', to: 'completed' }
            };
            const transition = eventTransitions[event];

            if (
              transition &&
              findIndex(
                prev[transition.to].tournaments,
                element => element.id === id
              ) === -1
            ) {
              let fromTournaments = prev[transition.from].tournaments;

              const tournamentIdx = findIndex(
                fromTournaments,
                item => item.id === id
              );

              if (tournamentIdx === -1) {
                return null;
              }

              const oldTournament = fromJS(fromTournaments[tournamentIdx]);
              const newTournament = oldTournament
                .set(
                  'status',
                  oldTournament.get('status') === TOURNAMENT_STATUS_UPCOMING
                    ? TOURNAMENT_STATUS_IN_PROGRESS
                    : TOURNAMENT_STATUS_COMPLETED
                )
                .update('leaderBoard', val =>
                  event === TOURNAMENT_EVENT_END && !val
                    ? { __typename: 'TournamentLeaderBoard', rankings: null }
                    : val
                );

              return fromJS(prev)
                .updateIn([transition.to, 'tournaments'], arr =>
                  arr.push(newTournament)
                )
                .updateIn([transition.from, 'tournaments'], tournaments =>
                  tournaments.filter(tournament => tournament.get('id') !== id)
                )
                .toJS();
            }

            return null;
          }
        });
      },
      subscribeToLocationData: () => {
        return data.subscribeToMore({
          document: SUBSCRIPTION_PREFERRED_LOCATION_CHANGED,
          updateQuery: () => data.refetch()
        });
      }
    })
  })
)(TournamentList);
